
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar as CalendarIcon, FileSpreadsheet, FileText, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ExportEventsModal = ({ open, onOpenChange, events, currentView }) => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const [formatType, setFormatType] = useState("csv");
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      if (formatType === "csv") {
        const csvData = convertToCSV(events);
        downloadFile(csvData, "events.csv", "text/csv");
      } else if (formatType === "pdf") {
        await generatePDF(events);
      } else {
        const jsonData = JSON.stringify(events, null, 2);
        downloadFile(jsonData, "events.json", "application/json");
      }
      toast({
        title: t('agenda.exportComplete'),
        description: t('agenda.exportCompleteDesc'),
      });
    } catch (error) {
      toast({
        title: t('agenda.exportFailed'),
        description: t('agenda.exportFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      onOpenChange(false);
    }
  };

  const convertToCSV = (events) => {
    const headers = ["Title", "Start", "End", "Category", "Location", "Students", "Note"];
    const rows = events.map(event => {
      const startDate = format(new Date(event.start), 'yyyy-MM-dd HH:mm');
      const endDate = format(new Date(event.end), 'yyyy-MM-dd HH:mm');
      const category = event.extendedProps?.categoryName || '';
      const location = event.extendedProps?.locationName || '';
      const students = event.extendedProps?.studentNames?.join(', ') || '';
      return [event.title, startDate, endDate, category, location, students, event.extendedProps?.note || ''];
    });
    return [headers, ...rows].map(row => row.join(",")).join("\n");
  };

  const downloadFile = (data, filename, type) => {
    const file = new Blob([data], { type: type });
    const a = document.createElement("a");
    const url = URL.createObjectURL(file);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  };

  const generatePDF = async (events) => {
    const doc = new jsPDF();
    const tableColumn = ["Title", "Start", "End", "Category", "Location", "Students", "Note"];
    const tableRows = [];

    events.forEach(event => {
      const startDate = format(new Date(event.start), 'yyyy-MM-dd HH:mm');
      const endDate = format(new Date(event.end), 'yyyy-MM-dd HH:mm');
      const category = event.extendedProps?.categoryName || '';
      const location = event.extendedProps?.locationName || '';
      const students = event.extendedProps?.studentNames?.join(', ') || '';
      const rowData = [
        event.title,
        startDate,
        endDate,
        category,
        location,
        students,
        event.extendedProps?.note || ''
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
    });

    doc.save('events.pdf');
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : ""}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-4" : ""}>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              {t('agenda.exportEvents')}
            </div>
          </DialogTitle>
          <DialogDescription>
            {t('agenda.exportEventsDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className={isMobile ? "max-h-[calc(100dvh-180px)] px-6" : "max-h-[60vh]"}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <Card className="p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors flex flex-col items-center justify-center text-center" onClick={() => setFormatType('csv')}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formatType === 'csv' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div className="font-medium">CSV</div>
                <div className="text-xs text-muted-foreground">Excel compatible</div>
              </Card>
              
              <Card className="p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors flex flex-col items-center justify-center text-center" onClick={() => setFormatType('json')}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formatType === 'json' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                  <FileText className="h-5 w-5" />
                </div>
                <div className="font-medium">JSON</div>
                <div className="text-xs text-muted-foreground">Raw data</div>
              </Card>
              
              <Card className="p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors flex flex-col items-center justify-center text-center" onClick={() => setFormatType('pdf')}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${formatType === 'pdf' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                  <FileText className="h-5 w-5" />
                </div>
                <div className="font-medium">PDF</div>
                <div className="text-xs text-muted-foreground">Print ready</div>
              </Card>
            </div>
          </div>
        </ScrollArea>
        
        <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-background z-20" : ""}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="default" 
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? t('common.processing') : t('agenda.exportNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportEventsModal;
